import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { OtpCodeSchema, PhoneSchema, type CountryCode } from '@capsico/contracts';
import { startCustomerSignIn, verifyCustomerSignIn } from '../api/auth';
import { errorMessage } from '../api/client';
import { turnstileErrorKind } from '../api/turnstile';
import { useI18n } from '../i18n';
import { useFormDraft } from './useFormDraft';
import { useTurnstile } from './useTurnstile';

const DIAL: Record<CountryCode, string> = { IN: '+91', NP: '+977' };

interface PhoneForm {
  country: CountryCode;
  number: string;
}

const EMPTY: PhoneForm = { country: 'IN', number: '' };

/**
 * Phone → 6-digit code sign-in for customers. Only the phone number is kept as a draft; the code never is.
 * W25: in the browser build the code request (and every resend) carries a Turnstile token; the
 * native app has no widget and sends none.
 */
export function useSignIn() {
  const client = useQueryClient();
  const { t } = useI18n();
  const turnstile = useTurnstile();
  const [form, setForm] = useState<PhoneForm>(EMPTY);
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState('');
  const [smsDelayed, setSmsDelayed] = useState(false);
  const [resendAt, setResendAt] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  // Only the phone number is kept as a draft; the code never is.
  const draft = useFormDraft<PhoneForm>('customer-sign-in:new', form, {
    enabled: true,
    isEmpty: (value) => value.number.trim() === '',
    base: EMPTY,
  });

  useEffect(() => {
    if (step !== 'code') return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [step]);

  const phone = useMemo(() => `${DIAL[form.country]}${form.number.replace(/\D/g, '')}`, [form]);

  const start = useMutation({
    mutationFn: (token: string | null) => startCustomerSignIn(phone, token),
    // Turnstile tokens are single-use: get a fresh check after every attempt.
    onSettled: () => turnstile.reset(),
    onSuccess: (result) => {
      setSentTo(result.sentTo);
      setSmsDelayed(result.smsDelayed);
      setResendAt(Date.now() + result.resendInSeconds * 1000);
      setNow(Date.now());
      setCode('');
      setStep('code');
      setError(null);
    },
    onError: (failure) => {
      const refused = turnstileErrorKind(failure);
      setError(refused ? t(`turnstile.${refused}`) : errorMessage(failure));
    },
  });

  /** Ask for a code; in the browser only once the Turnstile check has passed. */
  const requestCode = () => {
    if (turnstile.enabled && !turnstile.token) {
      setError(t('turnstile.required'));
      return;
    }
    setError(null);
    start.mutate(turnstile.enabled ? turnstile.token : null);
  };

  const verify = useMutation({
    mutationFn: (digits: string) => verifyCustomerSignIn(phone, digits),
    onSuccess: (me) => {
      draft.clear();
      setCode('');
      client.setQueryData(['me'], me);
      void client.invalidateQueries({ queryKey: ['profile'] });
      void client.invalidateQueries({ queryKey: ['addresses'] });
    },
    onError: (failure) => {
      setCode('');
      setError(errorMessage(failure));
    },
  });

  const resendIn = Math.max(0, Math.ceil((resendAt - now) / 1000));

  return {
    step,
    form,
    dial: DIAL[form.country],
    setCountry: (country: CountryCode) => setForm((current) => ({ ...current, country })),
    setNumber: (number: string) => {
      setError(null);
      setForm((current) => ({ ...current, number: number.replace(/[^\d ]/g, '').slice(0, 12) }));
    },
    draftRestored: draft.restored !== null,
    keepDraft: () => {
      const kept = draft.keep();
      if (kept) setForm(kept);
    },
    discardDraft: draft.discard,
    sendCode: () => {
      const parsed = PhoneSchema.safeParse(phone);
      if (!parsed.success) {
        setError(parsed.error.issues[0]?.message ?? 'Enter your mobile number');
        return;
      }
      requestCode();
    },
    sending: start.isPending,
    code,
    setCode: (value: string) => {
      setError(null);
      const digits = value.replace(/\D/g, '').slice(0, 6);
      setCode(digits);
      // Auto-submit once all six digits are in.
      if (OtpCodeSchema.safeParse(digits).success && !verify.isPending) verify.mutate(digits);
    },
    verify: () => {
      if (!OtpCodeSchema.safeParse(code).success) {
        setError('Enter the 6-digit code');
        return;
      }
      verify.mutate(code);
    },
    verifying: verify.isPending,
    sentTo,
    smsDelayed,
    resendIn,
    resend: requestCode,
    changeNumber: () => {
      setStep('phone');
      setCode('');
      setError(null);
    },
    error,
    turnstile: {
      enabled: turnstile.enabled,
      ref: turnstile.ref,
      error: turnstile.failed ? t('turnstile.failed') : null,
    },
  };
}

export type SignInState = ReturnType<typeof useSignIn>;
